import * as React from 'react';
import { trackEvent } from '../../services/metrics';
import { formatNumber } from '@progress/kendo-intl';
import { NumericTextBox, NumericTextBoxChangeEvent, Slider, SliderChangeEvent } from '@progress/kendo-react-inputs';
import { fetchApi } from '../../services/api';
import './Quote.css';
import { Button } from '@progress/kendo-react-buttons';
import { TabStrip, TabStripSelectEventArguments, TabStripTab } from '@progress/kendo-react-layout';
import LoadingPanel from '../../components/LoadingPanel';
import { Loader, Skeleton } from '@progress/kendo-react-indicators';
import { bankersRounding } from 'utils/bankersRounding';
import { SvgIcon } from "@progress/kendo-react-common";
import { caretAltDownIcon, caretAltUpIcon, xIcon } from '@progress/kendo-svg-icons';
import { Chart, ChartCategoryAxis, ChartCategoryAxisItem, ChartSeries, ChartSeriesItem, ChartTooltip, ChartValueAxis, ChartValueAxisItem } from '@progress/kendo-react-charts';
import AddChargeDialog from './AddChargeDialog';

type State = {
  chargeTypes: ChargeType[];
  winPercentageDataLoading: boolean;
  showModal: boolean;
  allInRate: number;
  selected: number;
  mpoLookbackDays: 30 | 60 | 90;
  showPricingPoints: boolean;
  pricingWinWeight: number | null;
  pricingModeLoading: 'Driver' | 'Carrier' | null;
}

type MpoAllInRateButton =
  | 'current-customer-max'
  | 'all-customers-max'
  | 'current-customer-avg'
  | 'all-customers-avg'
  | 'current-customer-min'
  | 'all-customers-min';

export type ChargeType = {
  RateDescriptionID: number;
  RateType: number;
  Description: string;
}

type Props = {
  QuoteID: number;
  distance: number;
  data: QuoteValue | null;
  smartbidData: SmartbidValue | null;
  winPercentageData: WinPercentageChartValue | null;
  mpoAllInRateData: MpoAllInRateValue | null;
  mpoAllInRateLoading: boolean;
  virtualTerminalReport: string;
  pricingRecommendation: PricingRecommendationValue | null;
  pricingRecommendationLoading: boolean;
  loading: boolean;
  outdated: boolean;
  smartbidLoading: boolean;
  tolls: number;
  fuelRate: number;
  vehicleTypeId: number;
  billingCustomerId: number;
  updateCharge: (index: number, data: { Amount: number }) => void;
  addCharge: (data: { ChargeType: ChargeType, Quantity: number, QuantityUoM: number, Rate: number, RateUoM: number, Amount: number }) => void;
  removeCharge: (index: number) => void;
  replaceCharges: (
    data: Array<{ ChargeType: ChargeType, Quantity: number, QuantityUoM: number, Rate: number, RateUoM: number, Amount: number }>,
    smartbidUsed?: boolean,
    smartbidRateUpDown?: number,
    smartbidWinPercentagePrevious?: number,
    smartbidWinPercentageCurrent?: number
  ) => void;
  fetchWinPercentageChart: () => Promise<void>;
  recalculateRate: () => void;
  fetchVirtualTerminalReport: () => Promise<void>;
  fetchPricingRecommendation: (mode?: 'Driver' | 'Carrier') => void;
  fetchMpoAllInRate: (lookbackDays: 30 | 60 | 90) => void;
};

export type QuoteValue = {
  Amount: number;
  LineHaulAmount: number;
  FuelAmount: number;
  AccessorialAmount: number;
  BonusAmount: number;
  RateComment: string;
  LineItems: QuoteLineItemValue[];
}

export type SmartbidValue = {
  Current: WinRatePercentageData;
  Next: WinRatePercentageData;
}

export type WinRatePercentageData = {
  RPM: number;
  LinehaulRPM: number;
  Min: number;
  Max: number;
  Mean: number;
  Increase: boolean;
}

export type WinPercentageChartValue = {
  CurrentBidIndex: number;
  Percentile1: Array<{ RPM: number, LinehaulRPM: number, Min: number, Max: number, Mean: number }>;
}

export type PricingRecommendationValue = {
  RatingEngineTotal: number;
  RatingEngineWinPercent: number;
  RecommendedTotal: number;
  RecommendedWinPercent: number;
  BandMinTotal: number;
  BandMaxTotal: number;
  AllCustomersMinTotal: number;
  AllCustomersMaxTotal: number;
  CurrentCustomerMinTotal: number;
  CurrentCustomerMaxTotal: number;
  Mpo: MpoAllInRateValue | null;
  Reason: string;
  Mode: 'Driver' | 'Carrier';
  ModeReason: string;
  SuggestedMode: 'Driver' | 'Carrier';
  CarrierCost: number | null;
  DriverOfferPay: number | null;
  DriverOfferFloorTotal: number | null;
  CostBasis: 'Driver' | 'Carrier';
  Points: PricingPoint[];
  WinWeight: number;
  MinMargin: number;
  Candidates: PricingPoint[];
}

type PricingPoint = {
  Total: number;
  WinPercent: number;
  Cost: number;
  IsRecommended: boolean;
  IsRatingEngine: boolean;
  BelowMarginFloor: boolean;
  BelowDriverOffer: boolean;
}

export type MpoAllInRateValue = {
  Miles: number;

  AllCustomersAvgRPM: number;
  AllCustomersMaxRPM: number;
  AllCustomersMinRPM: number;
  AllCustomersAllInAvgRate: number;
  AllCustomersAllInMaxRate: number;
  AllCustomersAllInMinRate: number;
  AllCustomersLoadCount: number;
  AllCustomersSource: string;
  AllCustomersMatchLevel: string;
  AllCustomersMileageBucket: string;

  CurrentCustomerAvgRPM: number;
  CurrentCustomerMaxRPM: number;
  CurrentCustomerMinRPM: number;
  CurrentCustomerAllInAvgRate: number;
  CurrentCustomerAllInMaxRate: number;
  CurrentCustomerAllInMinRate: number;
  CurrentCustomerLoadCount: number;
  CurrentCustomerSource: string;
  CurrentCustomerMatchLevel: string;
  CurrentCustomerMileageBucket: string;

  DriverAvgRPM: number;
  DriverMaxRPM: number;
  DriverMinRPM: number;
  DriverMileageBucket: string;

  CarrierAvgRPM: number;
  CarrierMaxRPM: number;
  CarrierMinRPM: number;
  CarrierMileageBucket: string;

  DriverMinMarginPercent: number;
  DriverAvgMarginPercent: number;
  DriverMaxMarginPercent: number;

  CarrierMinMarginPercent: number;
  CarrierAvgMarginPercent: number;
  CarrierMaxMarginPercent: number;

  DriverAllInAvgRate: number;
  DriverAllInMaxRate: number;
  DriverAllInMinRate: number;

  CarrierAllInAvgRate: number;
  CarrierAllInMaxRate: number;
  CarrierAllInMinRate: number;

  DriverLoadCount: number;
  CarrierLoadCount: number;

  DriverSource: string;
  CarrierSource: string;

  DriverMatchLevel: string;
  CarrierMatchLevel: string;

  BillToCustomerNumber: string;
};

export type SmartbidRate = {
  Rate: number | null;
  Confidence: number;
  AdjustingUp: boolean | null;
  UsingAllCustomerData: boolean;
}

export type SmartBidHistoricalData = {
  LinehaulRPM: number;
  LinehaulCharge: number;
  LinehaulPay: number;
  Margin: number;
  MarginPercentage: number;
  CustomerNumber: string;
  OrderID: number;
  OrderNumber: string;
  Distance: number;
  Description: string;
}

export type QuoteLineItemValue = {
  RateDescriptionID: number;
  RateType: number;
  Name: string;
  Quantity: number;
  QuantityUoM: number;
  Rate: number;
  RateUoM: number;
  Amount: number;
  OriginalAmount: number | null;
  IsAutoRated: boolean;
}

export default class QuotePanel extends React.Component<Props, State> {

  constructor(props: Props) {
    super(props);

    this.state = {
      chargeTypes: [],
      winPercentageDataLoading: false,
      showModal: false,
      allInRate: 0,
      showPricingPoints: false,
      pricingWinWeight: null,
      pricingModeLoading: null,
      selected: 0,
      mpoLookbackDays: 30,
    }

    this.removeCharge = this.removeCharge.bind(this);
    this.showModal = this.showModal.bind(this);
    this.hideModal = this.hideModal.bind(this);
    this.setPerMileRate = this.setPerMileRate.bind(this);
    this.setAllInRate = this.setAllInRate.bind(this);
    this.useHistoricalRate = this.useHistoricalRate.bind(this);
    this.calculateAllInRate = this.calculateAllInRate.bind(this);
    this.handleTabSelect = this.handleTabSelect.bind(this);
    this.applyMpoAllInRate = this.applyMpoAllInRate.bind(this);
  }

  render() {
    if (!this.props.data) {
      return null;
    }

    const total67 = this.props.data.Amount - ((this.props.data.LineHaulAmount * 0.67) + this.props.data.FuelAmount + this.props.tolls);
    const total70 = this.props.data.Amount - ((this.props.data.LineHaulAmount * 0.70) + this.props.data.FuelAmount + this.props.tolls);
    const allInRate = this.state.allInRate > 0 ? this.state.allInRate : '';
    //const mpoMaxAllInRate = this.props.mpoAllInRateData ? Math.round(this.props.mpoAllInRateData.CustomerAllInMaxRate * 100) / 100 : 0;
    //const mpoAvgAllInRate = this.props.mpoAllInRateData ? Math.round(this.props.mpoAllInRateData.CustomerAllInAvgRate * 100) / 100 : 0;
    //const mpoMinAllInRate = this.props.mpoAllInRateData ? Math.round(this.props.mpoAllInRateData.CustomerAllInMinRate * 100) / 100 : 0;
    const fuelLineItem = this.props.data.LineItems.find(x => [5000, 5001, 5003].includes(x.RateDescriptionID) && (x.Rate > 0 || x.Amount > 0));
    const fuelRateDisplay = fuelLineItem
      ? fuelLineItem.RateDescriptionID === 5001
        ? formatNumber(fuelLineItem.Rate ?? 0, "p0")
        : formatNumber(fuelLineItem.Amount ?? 0, "c2")
      : formatNumber(0, "c2");

    return (
      <TabStrip
        selected={this.state.selected}
        onSelect={this.handleTabSelect}
        style={{ overflowY: 'auto', height: '100%' }}
        animation={false}
      >
        <TabStripTab title="Rate">
          {this.props.loading && <LoadingPanel />}
          {this.props.outdated && <div className="alert alert-warning" role="alert">
            <a href="#" onClick={(e) => { e.preventDefault(); this.props.recalculateRate(); }}>Recalculate Rate</a> if necessary as the quote has changed.
          </div>}
          <h5 className="card-title">
            <div className="row">
              <div className="col-lg-7">
                {formatNumber(this.props.data.Amount, "c")}
                <span style={{ fontWeight: 'normal', fontSize: '8pt' }}>
                  &nbsp; <span style={{ color: 'orange' }}>67%: {formatNumber(total67, "c")} ({formatNumber(total67 / this.props.data.Amount, "p")})</span>
                  &nbsp; <span style={{ color: 'green' }}>70%: {formatNumber(total70, "c")} ({formatNumber(total70 / this.props.data.Amount, "p")})</span>
                </span>
              </div>
              <div className="col-lg-5">
                <div className="input-group">
                  <input type="number" className="form-control form-control-sm" placeholder="Rate" value={allInRate} onChange={(e) => this.setState({ allInRate: parseFloat(e.target.value) || 0 })} />
                  <div className="input-group-append">
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={this.setPerMileRate}>Per Mile</button>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={this.setAllInRate}>All In</button>
                  </div>
                </div>
              </div>
            </div>
          </h5>
          <table className="table">
            <caption>{this.props.data.RateComment}{this.props.fuelRate > 0 && ` (FSC ${formatNumber(this.props.fuelRate, "p2")})`}</caption>
            <thead>
              <tr>
                <th>Line Item</th>
                <th className="text-muted text-right">Qty</th>
                <th className="text-muted text-right">Rate</th>
                <th className="text-right">Amount</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {this.props.data.LineItems.map((lineItem, index) => {
                return <tr key={index}>
                  <td>{lineItem.Name}</td>
                  <td className="text-muted text-right">{formatNumber(lineItem.Quantity, 'n2')}</td>
                  <td className="text-muted text-right">{formatNumber(lineItem.Rate, (lineItem.RateType == 84 || lineItem.RateType == 85) ? 'n5' : lineItem.RateType == 76 ? 'n3' : 'n2')}</td>
                  <td className="text-right">
                    {(lineItem.Quantity === 1 && lineItem.IsAutoRated === false)
                      ? <NumericTextBox spinners={false} value={lineItem.Amount} format="c2" min={0} onChange={(e) => this.updateChargeAmount(e, index)} />
                      : (
                        lineItem.IsAutoRated && lineItem.OriginalAmount && lineItem.OriginalAmount !== lineItem.Amount ? (
                          <span>
                            <span className="text-muted" style={{ textDecoration: 'line-through', marginRight: 6 }}>{formatNumber(lineItem.OriginalAmount, "c")}</span>
                            <span>{formatNumber(lineItem.Amount, "c")}</span>
                          </span>
                        ) : formatNumber(lineItem.Amount, "c")
                      )
                    }
                  </td>
                  <td><a href="#" onClick={(e) => this.removeCharge(e, index)}><SvgIcon icon={xIcon} /></a></td>
                </tr>
              })}
              <tr>
                <td colSpan={5}>
                  <button type="button" className="btn btn-link" onClick={this.showModal}>+ Add Charge</button>
                  {this.state.showModal && <AddChargeDialog
                    data={this.props.data}
                    distance={this.props.distance}
                    fuelRate={this.props.fuelRate}
                    vehicleTypeId={this.props.vehicleTypeId}
                    billingCustomerId={this.props.billingCustomerId}
                    chargeTypes={this.state.chargeTypes}
                    close={this.hideModal}
                    addCharge={this.props.addCharge}
                  />}
                </td>
              </tr>
            </tbody>
          </table>
          <div className="mt-3 mb-2">
            {this.props.pricingRecommendation
              ? this.renderPricingRecommendation(this.props.pricingRecommendation)
              : this.props.pricingRecommendationLoading && this.renderPricingRecommendationSkeleton()}
          </div>
          {(this.props.smartbidLoading && !this.props.smartbidData) && <Loader size="small" />}
          {this.props.smartbidData && <>
            {this.props.smartbidData.Current && <Button
              disabled
              size="small"
              className="mr-1"
              style={{ backgroundImage: `linear-gradient(to right, rgb(108, 117, 125) ${this.props.smartbidData.Current.Mean * 100}%, transparent ${this.props.smartbidData.Current.Mean * 100}%)` }}
            >
              {formatNumber(this.props.smartbidData.Current.RPM, 'c2')}/mi (Win {formatNumber(this.props.smartbidData.Current.Mean, 'p')})
            </Button>}
            {this.props.smartbidData.Next && this.props.smartbidData.Next.Increase === false && <Button
              size="small"
              className="mr-1"
              icon="arrow-s"
              svgIcon={caretAltDownIcon}
              style={{ backgroundImage: `linear-gradient(to right, rgba(108, 117, 125, .5) 0 ${this.props.smartbidData.Current.Mean * 100}%, rgb(25, 135, 84) ${this.props.smartbidData.Current.Mean * 100}% ${this.props.smartbidData.Next.Mean * 100}%, transparent ${this.props.smartbidData.Next.Mean * 100}%)` }}
              onClick={() => this.useHistoricalRate(this.props.smartbidData.Next.LinehaulRPM, this.props.smartbidData.Next.LinehaulRPM - this.props.smartbidData.Current.LinehaulRPM, this.props.smartbidData.Current.Mean, this.props.smartbidData.Next.Mean)}
            >
              {formatNumber(this.props.smartbidData.Next.RPM, 'c2')}/mi (Win +{formatNumber(this.props.smartbidData.Next.Mean - this.props.smartbidData.Current.Mean, 'p2')})
            </Button>}
            {this.props.smartbidData.Next && this.props.smartbidData.Next.Increase && <Button
              size="small"
              className="mr-1"
              icon="arrow-n"
              svgIcon={caretAltUpIcon}
              style={{ backgroundImage: `linear-gradient(to right, rgba(108, 117, 125, .5) 0 ${this.props.smartbidData.Next.Mean * 100}%, rgb(220, 53, 69) ${this.props.smartbidData.Current.Mean * 100}%, transparent ${this.props.smartbidData.Current.Mean * 100}%)` }}
              onClick={() => this.useHistoricalRate(this.props.smartbidData.Next.LinehaulRPM, this.props.smartbidData.Next.LinehaulRPM - this.props.smartbidData.Current.LinehaulRPM, this.props.smartbidData.Current.Mean, this.props.smartbidData.Next.Mean)}
            >
              {formatNumber(this.props.smartbidData.Next.RPM, 'c2')}/mi ({this.props.smartbidData.Next.Mean - this.props.smartbidData.Current.Mean === 0 ? "Same Win %" : "Win " + formatNumber(this.props.smartbidData.Next.Mean - this.props.smartbidData.Current.Mean, 'p2')})
            </Button>}
          </>}
        </TabStripTab>
        <TabStripTab title="Win %" disabled={this.state.winPercentageDataLoading}>
          {this.props.winPercentageData && <Chart
            transitions={false}
            style={{ height: 341 }}
            onSeriesClick={(e) => this.useHistoricalRate(Math.round(e.dataItem.LinehaulRPM * 100) / 100, (Math.round(e.dataItem.LinehaulRPM * 100) / 100) - this.props.winPercentageData.Percentile1[this.props.winPercentageData.CurrentBidIndex].LinehaulRPM, this.props.winPercentageData.Percentile1[this.props.winPercentageData.CurrentBidIndex].Mean, e.stackValue)}
          >
            <ChartSeries>
              <ChartSeriesItem
                color="rgb(25,135,84)"
                type="rangeArea"
                data={this.props.winPercentageData.Percentile1}
                fromField="Min"
                toField="Max"
                meanField="Mean"
                categoryField="RPM"
                opacity={.95}
              />
              <ChartSeriesItem
                color="#000000"
                type="line"
                data={this.props.winPercentageData.Percentile1}
                field="Mean"
                markers={{ visible: false }}
                opacity={1}
              >
                <ChartTooltip format='{0:p2}' background="#000000" color="#ffffff" />
              </ChartSeriesItem>
            </ChartSeries>
            <ChartValueAxis>
              <ChartValueAxisItem labels={{ format: "{0:p2}" }} />
            </ChartValueAxis>
            <ChartCategoryAxis>
              <ChartCategoryAxisItem labels={{ rotation: "auto", format: "{0:c}", step: 3 }} plotBands={[{ from: this.props.winPercentageData.CurrentBidIndex, to: this.props.winPercentageData.CurrentBidIndex, color: "#dc3545", label: { rotation: 90, text: "Current Bid", color: 'white', background: '#dc3545', padding: { left: 5, right: 5 } } }]} />
            </ChartCategoryAxis>
            <ChartTooltip format='{0:p2} - {1:p2}' />
          </Chart>}
          <p className="text-muted small mt-2 mb-1">The win rate % indicated is provided by a data science model which takes the set of attributes on this bid, compares them to historical LoadOne bid information, and determines our probability of winning based upon the per mile line haul fee. The model is being updated nightly based upon the prior 30 days of data.</p>
          <p className="text-muted small mb-0">Dispatchers should leverage information provided by this model as another tool in their toolbox when determining a bid. The model does not know everything about the real world, so dispatchers have autonomy to trust their gut.</p>
        </TabStripTab>
        <TabStripTab title="MPO">
          <div className="mpo-all-in-rate-container mt-3 mb-2">
            <div className="mpo-all-in-rate-wrap" style={{ position: 'relative' }}>
              {this.props.mpoAllInRateLoading && (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10,
                    pointerEvents: 'none'
                  }}
                >
                  <Loader size="large" type="infinite-spinner" />
                </div>
              )}
              <table className="mpo-all-in-rate-table">
                {!this.props.mpoAllInRateLoading && !this.props.mpoAllInRateData && <caption style={{ textAlign: 'center' }}>No records to show</caption>}
                <thead>
                  <tr>
                    <th colSpan={5} className="mpo-all-in-rate-title">
                      <div className="mpo-all-in-rate-title-content">
                        <select
                          className="form-control form-control-sm"
                          value={this.state.mpoLookbackDays}
                          onChange={(e) => {
                            const lookbackDays = parseInt(e.target.value, 10) as 30 | 60 | 90;
                            this.setState({ mpoLookbackDays: lookbackDays });
                            this.props.fetchMpoAllInRate(lookbackDays);
                          }}
                        >
                          <option value={30}>30 Day</option>
                          <option value={60}>60 Day</option>
                          <option value={90}>90 Day</option>
                        </select>
                        <span>Pricing Metrics (Loads &amp; Carrier Bids)</span>
                      </div>
                    </th>
                  </tr>
                  {this.props.mpoAllInRateData && <>
                    <tr>
                      <th />
                      <th colSpan={2}>Expected Pricing</th>
                      <th colSpan={2}>Expected Cost</th>
                    </tr>
                    <tr>
                      <th style={{ whiteSpace: 'normal' }}>{formatNumber(this.props.mpoAllInRateData.Miles, "n0")} miles, {fuelRateDisplay} FSC</th>
                      <th>{this.props.mpoAllInRateData.BillToCustomerNumber}</th>
                      <th>Other Customers</th>
                      <th title="Uses minimum, median, and maximum accepted driver offers grouped by vehicle type, rounded distance, and lane.">Driver Offers</th>
                      <th title="Uses minimum, median, and maximum carrier bids grouped by vehicle type, rounded distance, and lane.">Carrier Bids</th>
                    </tr>
                  </>}
                </thead>
                <tbody>
                  {this.props.mpoAllInRateData && (
                    <>
                      <tr>
                        <td>Max. RPM</td>
                        <td>
                          <span className="metric-value">{formatNumber(this.props.mpoAllInRateData.CurrentCustomerMaxRPM, "c2")}</span>
                          {this.props.mpoAllInRateData.CurrentCustomerAllInMaxRate > 0 &&
                            <button type="button" className="btn btn-link btn-sm p-0 metric-meta metric-meta-link" onClick={() => this.applyMpoAllInRate(this.props.mpoAllInRateData.CurrentCustomerAllInMaxRate, 'current-customer-max')}>{formatNumber(this.props.mpoAllInRateData.CurrentCustomerAllInMaxRate, "c")} All In</button>}
                        </td>
                        <td>
                          <span className="metric-value">{formatNumber(this.props.mpoAllInRateData.AllCustomersMaxRPM, "c2")}</span>
                          {this.props.mpoAllInRateData.AllCustomersAllInMaxRate > 0 &&
                            <button type="button" className="btn btn-link btn-sm p-0 metric-meta metric-meta-link" onClick={() => this.applyMpoAllInRate(this.props.mpoAllInRateData.AllCustomersAllInMaxRate, 'all-customers-max')}>{formatNumber(this.props.mpoAllInRateData.AllCustomersAllInMaxRate, "c")} All In</button>}
                        </td>
                        <td>
                          <span className="metric-value">{formatNumber(this.props.mpoAllInRateData.DriverMaxRPM, "c2")}</span>
                          <span className="metric-meta">{formatNumber(this.props.mpoAllInRateData.DriverAllInMaxRate, "c2")} All In</span>
                          <span className={`metric-meta ${this.getMpoMarginClass(this.props.mpoAllInRateData.DriverMaxMarginPercent, this.props.mpoAllInRateData.CarrierMaxMarginPercent)}`}>
                            {formatNumber(this.props.mpoAllInRateData.DriverMaxMarginPercent, "p1")} Margin
                          </span>
                        </td>
                        <td>
                          <span className="metric-value">{formatNumber(this.props.mpoAllInRateData.CarrierMaxRPM, "c2")}</span>
                          <span className="metric-meta">{formatNumber(this.props.mpoAllInRateData.CarrierAllInMaxRate, "c2")} All In</span>
                          <span className={`metric-meta ${this.getMpoMarginClass(this.props.mpoAllInRateData.CarrierMaxMarginPercent, this.props.mpoAllInRateData.DriverMaxMarginPercent)}`}>
                            {formatNumber(this.props.mpoAllInRateData.CarrierMaxMarginPercent, "p1")} Margin</span>
                        </td>
                      </tr>
                      <tr>
                        <td title="Customer figures use averages; driver figures are based on median accepted driver offers and carrier figures on median bids.">Avg. / Median RPM</td>
                        <td>
                          <span className="metric-value">{formatNumber(this.props.mpoAllInRateData.CurrentCustomerAvgRPM, "c2")}</span>
                          {this.props.mpoAllInRateData.CurrentCustomerAllInAvgRate > 0 &&
                            <button type="button" className="btn btn-link btn-sm p-0 metric-meta metric-meta-link" onClick={() => this.applyMpoAllInRate(this.props.mpoAllInRateData.CurrentCustomerAllInAvgRate, 'current-customer-avg')}>{formatNumber(this.props.mpoAllInRateData.CurrentCustomerAllInAvgRate, "c")} All In</button>}
                        </td>
                        <td>
                          <span className="metric-value">{formatNumber(this.props.mpoAllInRateData.AllCustomersAvgRPM, "c2")}</span>
                          {this.props.mpoAllInRateData.AllCustomersAllInAvgRate > 0 &&
                            <button type="button" className="btn btn-link btn-sm p-0 metric-meta metric-meta-link" onClick={() => this.applyMpoAllInRate(this.props.mpoAllInRateData.AllCustomersAllInAvgRate, 'all-customers-avg')}>{formatNumber(this.props.mpoAllInRateData.AllCustomersAllInAvgRate, "c")} All In</button>}
                        </td>
                        <td>
                          <span className="metric-value" title="Mileage-weighted RPM based on each group's median accepted driver offer.">{formatNumber(this.props.mpoAllInRateData.DriverAvgRPM, "c2")}</span>
                          <span className="metric-meta">{formatNumber(this.props.mpoAllInRateData.DriverAllInAvgRate, "c2")} All In</span>
                          <span className={`metric-meta ${this.getMpoMarginClass(this.props.mpoAllInRateData.DriverAvgMarginPercent, this.props.mpoAllInRateData.CarrierAvgMarginPercent)}`}>
                            {formatNumber(this.props.mpoAllInRateData.DriverAvgMarginPercent, "p1")} Margin
                          </span>
                        </td>
                        <td>
                          <span className="metric-value" title="Mileage-weighted RPM based on each group's median carrier bid.">{formatNumber(this.props.mpoAllInRateData.CarrierAvgRPM, "c2")}</span>
                          <span className="metric-meta">{formatNumber(this.props.mpoAllInRateData.CarrierAllInAvgRate, "c2")} All In</span>
                          <span className={`metric-meta ${this.getMpoMarginClass(this.props.mpoAllInRateData.CarrierAvgMarginPercent, this.props.mpoAllInRateData.DriverAvgMarginPercent)}`}>
                            {formatNumber(this.props.mpoAllInRateData.CarrierAvgMarginPercent, "p1")} Margin
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td>Min. RPM</td>
                        <td>
                          <span className="metric-value">{formatNumber(this.props.mpoAllInRateData.CurrentCustomerMinRPM, "c2")}</span>
                          {this.props.mpoAllInRateData.CurrentCustomerAllInMinRate > 0 &&
                            <button type="button" className="btn btn-link btn-sm p-0 metric-meta metric-meta-link" onClick={() => this.applyMpoAllInRate(this.props.mpoAllInRateData.CurrentCustomerAllInMinRate, 'current-customer-min')}>{formatNumber(this.props.mpoAllInRateData.CurrentCustomerAllInMinRate, "c")} All In</button>}
                        </td>
                        <td>
                          <span className="metric-value">{formatNumber(this.props.mpoAllInRateData.AllCustomersMinRPM, "c2")}</span>
                          {this.props.mpoAllInRateData.AllCustomersAllInMinRate > 0 &&
                            <button type="button" className="btn btn-link btn-sm p-0 metric-meta metric-meta-link" onClick={() => this.applyMpoAllInRate(this.props.mpoAllInRateData.AllCustomersAllInMinRate, 'all-customers-min')}>{formatNumber(this.props.mpoAllInRateData.AllCustomersAllInMinRate, "c")} All In</button>}
                        </td>
                        <td>
                          <span className="metric-value">{formatNumber(this.props.mpoAllInRateData.DriverMinRPM, "c2")}</span>
                          <span className="metric-meta">{formatNumber(this.props.mpoAllInRateData.DriverAllInMinRate, "c2")} All In</span>
                          <span className={`metric-meta ${this.getMpoMarginClass(this.props.mpoAllInRateData.DriverMinMarginPercent, this.props.mpoAllInRateData.CarrierMinMarginPercent)}`}>
                            {formatNumber(this.props.mpoAllInRateData.DriverMinMarginPercent, "p1")} Margin
                          </span>
                        </td>
                        <td>
                          <span className="metric-value">{formatNumber(this.props.mpoAllInRateData.CarrierMinRPM, "c2")}</span>
                          <span className="metric-meta">{formatNumber(this.props.mpoAllInRateData.CarrierAllInMinRate, "c2")} All In</span>
                          <span className={`metric-meta ${this.getMpoMarginClass(this.props.mpoAllInRateData.CarrierMinMarginPercent, this.props.mpoAllInRateData.DriverMinMarginPercent)}`}>
                            {formatNumber(this.props.mpoAllInRateData.CarrierMinMarginPercent, "p1")} Margin
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td>Sample Count</td>
                        <td><span className="text-value">{formatNumber(this.props.mpoAllInRateData.CurrentCustomerLoadCount, "n0")}</span></td>
                        <td><span className="text-value">{formatNumber(this.props.mpoAllInRateData.AllCustomersLoadCount, "n0")}</span></td>
                        <td><span className="text-value">{formatNumber(this.props.mpoAllInRateData.DriverLoadCount, "n0")}</span></td>
                        <td><span className="text-value">{formatNumber(this.props.mpoAllInRateData.CarrierLoadCount, "n0")}</span></td>
                      </tr>
                      <tr>
                        <td className="section-row">History Source</td>
                        <td />
                        <td />
                        <td />
                        <td />
                      </tr>
                      <tr>
                        <td>Orders/Trips/Bids</td>
                        <td><span className="text-value">{this.props.mpoAllInRateData.CurrentCustomerSource}</span></td>
                        <td><span className="text-value">{this.props.mpoAllInRateData.AllCustomersSource}</span></td>
                        <td><span className="text-value">{this.props.mpoAllInRateData.DriverSource}</span></td>
                        <td><span className="text-value">{this.props.mpoAllInRateData.CarrierSource}</span></td>
                      </tr>
                      <tr>
                        <td>From - To</td>
                        <td><span className="text-value">{this.props.mpoAllInRateData.CurrentCustomerMatchLevel}</span></td>
                        <td><span className="text-value">{this.props.mpoAllInRateData.AllCustomersMatchLevel}</span></td>
                        <td><span className="text-value">{this.props.mpoAllInRateData.DriverMatchLevel}</span></td>
                        <td><span className="text-value">{this.props.mpoAllInRateData.CarrierMatchLevel}</span></td>
                      </tr>
                      <tr>
                        <td>Mileage Bucket</td>
                        <td><span className="text-value">{this.props.mpoAllInRateData.CurrentCustomerMileageBucket}</span></td>
                        <td><span className="text-value">{this.props.mpoAllInRateData.AllCustomersMileageBucket}</span></td>
                        <td><span className="text-value">{this.props.mpoAllInRateData.DriverMileageBucket}</span></td>
                        <td><span className="text-value">{this.props.mpoAllInRateData.CarrierMileageBucket}</span></td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabStripTab>
        <TabStripTab title="VT Report">
          {this.props.virtualTerminalReport ? <>
            <p>{this.props.virtualTerminalReport}</p>
            <p className='text-muted mt-2'>This is a very early version of the virtual terminal report. Please validate the information before using it.</p>
          </> : <Loader size="small" />}
        </TabStripTab>
      </TabStrip>)
  }

  private applyPricingRecommendation(data: PricingRecommendationValue, recommended: { Total: number, WinPercent: number }, winWeight: number) {
    trackEvent('quote.pricing_recommendation_applied', this.getPricingRecommendationEventPayload(data, recommended, winWeight));

    this.calculateAllInRate(recommended.Total);
  }

  private getPricingRecommendationEventPayload(data: PricingRecommendationValue, recommended: { Total: number, WinPercent: number }, winWeight: number) {
    return {
      QuoteID: this.props.QuoteID,
      BillingCustomerID: this.props.billingCustomerId,
      VehicleTypeID: this.props.vehicleTypeId,
      RatingEngineTotal: data.RatingEngineTotal,
      RatingEngineWinPercent: data.RatingEngineWinPercent,
      RecommendedTotal: recommended.Total,
      RecommendedWinPercent: recommended.WinPercent,
      ServerRecommendedTotal: data.RecommendedTotal,
      WinWeight: winWeight,
      BandMinTotal: data.BandMinTotal,
      BandMaxTotal: data.BandMaxTotal,
      AllCustomersLoadCount: data.Mpo?.AllCustomersLoadCount ?? 0,
      CurrentCustomerLoadCount: data.Mpo?.CurrentCustomerLoadCount ?? 0,
      Mode: data.Mode,
      CarrierCost: data.CarrierCost,
    };
  }

  // matchBackground: the recommended price's match color, so the row it came from shows the same color
  private formatPricingCost(cost: number, loadCount: number, matchLevel: string, recommendedTotal?: number, countLabel = "loads", costLabel = "avg", matchBackground?: string) {
    const margin = recommendedTotal ? (recommendedTotal - cost) / recommendedTotal : null;
    return <>
      {formatNumber(cost, "c0")} {costLabel} · {loadCount} {countLabel} · {matchBackground
        ? <span className="px-1 rounded" style={{ background: matchBackground, color: '#000' }}>{matchLevel}</span>
        : matchLevel}
      {margin !== null && ` (${formatNumber(margin, "p1")} margin at recommended)`}
    </>;
  }

  private renderPricingRecommendation(data: PricingRecommendationValue) {
    const mpo = data.Mpo;

    // Same weighted score the server picks with, re-run in the browser so the slider can try other win weights
    const winWeight = this.state.pricingWinWeight ?? data.WinWeight;
    const eligible = data.Candidates.filter(p => !p.BelowMarginFloor && !p.BelowDriverOffer);
    const bestWin = Math.max(0, ...eligible.map(p => p.WinPercent));
    const bestMargin = Math.max(0, ...eligible.map(p => p.Total - p.Cost));
    const weightedScore = (p: PricingPoint) =>
      winWeight * (bestWin > 0 ? p.WinPercent / bestWin : 0) + (1 - winWeight) * (bestMargin > 0 ? (p.Total - p.Cost) / bestMargin : 0);
    const winner = eligible.reduce<PricingPoint | null>((best, p) => !best || weightedScore(p) > weightedScore(best) ? p : best, null);

    const recommended = winner ? { Total: winner.Total, WinPercent: winner.WinPercent } : { Total: data.RecommendedTotal, WinPercent: data.RecommendedWinPercent };
    const difference = recommended.Total - data.RatingEngineTotal;
    const reason = winner && winWeight !== data.WinWeight
      ? `${formatNumber(winner.Total, "c0")} has the best weighted score (${formatNumber(winWeight, "p0")} win %, ${formatNumber(1 - winWeight, "p0")} margin $) between ${formatNumber(data.BandMinTotal, "c0")} and ${formatNumber(data.BandMaxTotal, "c0")}: ${formatNumber(winner.WinPercent, "p1")} predicted win vs ${formatNumber(data.RatingEngineWinPercent, "p1")} at the rating engine rate.`
      : data.Reason;

    const points = [...data.Points];
    if (winner && !points.some(p => p.Total === winner.Total)) {
      points.push({ ...winner, Cost: Math.round(winner.Cost) });
      points.sort((a, b) => a.Total - b.Total);
    }

    // How closely the MPO driver/carrier cost matched this lane, e.g. "481 - 442", "MI - 442", "MI - OH", "Any State - OH"
    const costLoadCount = data.Mode === 'Carrier' ? mpo?.CarrierLoadCount : mpo?.DriverLoadCount;
    const costMatchLevel = (data.Mode === 'Carrier' ? mpo?.CarrierMatchLevel : mpo?.DriverMatchLevel) ?? '';
    const [costFrom, costTo] = costMatchLevel.split(' - ');
    const isZip = (part?: string) => /^\d/.test(part ?? '');
    const costName = data.Mode.toLowerCase();
    const costMatch = !costLoadCount || !costTo
      ? { label: `No ${costName} cost found`, background: '#dc2626' }
      : costFrom === 'Any State'
        ? { label: `Any state to state ${costName} cost (${costMatchLevel})`, background: '#dc2626' }
        : isZip(costFrom) && isZip(costTo)
          ? { label: `Zip to zip ${costName} cost (${costMatchLevel})`, background: '#22c55e' }
          : isZip(costTo)
            ? { label: `State to zip ${costName} cost (${costMatchLevel})`, background: '#e3f6e8' }
            : { label: `State to state ${costName} cost (${costMatchLevel})`, background: '#f59e0b' };

    return (
      <div className="row">
        <div className="col-md-4">
          <div><span className="badge badge-primary">Pricing Recommendation</span></div>
          <h2 className="mt-2 mb-0 px-2 rounded d-inline-block font-weight-bold" style={{ background: costMatch.background, color: '#000' }} title={costMatch.label}>
            {formatNumber(recommended.Total, "c0")}
            {this.props.distance > 0 && <span className="font-weight-normal ml-2" style={{ fontSize: '14pt' }}>({formatNumber(recommended.Total / this.props.distance, "c2")}/mi)</span>}
          </h2>
          <div className="mt-2">
            {difference === 0 ? "Same as Rating Engine" : `${difference > 0 ? "+" : "−"}${formatNumber(Math.abs(difference), "c0")} vs. Rating Engine`}
          </div>
          <button type="button" className="btn btn-sm btn-primary mt-2" disabled={!this.props.data} onClick={() => this.applyPricingRecommendation(data, recommended, winWeight)}>
            Use {formatNumber(recommended.Total, "c0")} All In
          </button>
          <div className="mt-3">
            Asset to Use: <span className="font-weight-bold">{data.SuggestedMode === 'Carrier' ? 'Partner Carrier' : 'Load1 Driver'}</span>
          </div>
          {data.Candidates.length > 0 && <div className="mt-3">
            <div className="font-weight-bold">Price Influence Factors</div>
            <div className="small text-muted mb-2">Set the relative importance of winning vs. revenue contribution.</div>
            {[
              { id: 'pricing-win-weight', label: 'Win Probability', className: 'text-primary', sliderClassName: '', value: winWeight * 10, toWinWeight: (v: number) => v / 10 },
              { id: 'pricing-revenue-weight', label: 'Revenue Contribution', className: 'text-success', sliderClassName: 'pricing-slider-success', value: (1 - winWeight) * 10, toWinWeight: (v: number) => (10 - v) / 10 },
            ].map(factor => (
              <div key={factor.id} className="mb-2">
                <label htmlFor={factor.id} className="mb-0">
                  {factor.label} <span className={`ml-2 ${factor.className}`}>{Number(factor.value.toFixed(1))} out of 10</span>
                </label>
                <div className="d-flex align-items-center">
                  <span className="small mr-2">0</span>
                  <Slider
                    id={factor.id}
                    className={`flex-grow-1 ${factor.sliderClassName}`}
                    buttons={false}
                    min={0}
                    max={10}
                    step={1}
                    value={factor.value}
                    onChange={(e: SliderChangeEvent) => this.setState({ pricingWinWeight: factor.toWinWeight(Math.round(e.value)) })}
                  />
                  <span className="small ml-2">10</span>
                </div>
              </div>
            ))}
          </div>}
        </div>
        <div className="col-md-8">
          <table className="table table-sm mb-0">
            <tbody>
              <tr><td>Covered By</td><td className="text-value">
                <div className="btn-group btn-group-sm" role="group">
                  {(['Driver', 'Carrier'] as const).map(mode => (
                    <button
                      key={mode}
                      type="button"
                      className={`btn ${data.Mode === mode ? 'btn-primary' : 'btn-outline-secondary'}`}
                      disabled={this.props.pricingRecommendationLoading}
                      onClick={() => {
                        if (data.Mode !== mode) {
                          this.setState({ pricingModeLoading: mode });
                          this.props.fetchPricingRecommendation(mode);
                        }
                      }}
                    >
                      {mode}
                      {this.props.pricingRecommendationLoading && this.state.pricingModeLoading === mode && <Loader size="small" className="ml-1" />}
                    </button>
                  ))}
                </div>
                <div className="small text-muted">{data.ModeReason}</div>
              </td></tr>
              <tr><td>Rating Engine</td><td className="text-value">{formatNumber(data.RatingEngineTotal, "c0")} (Win {formatNumber(data.RatingEngineWinPercent, "p1")})</td></tr>
              {((mpo && mpo.DriverLoadCount > 0) || data.Mode === 'Driver') && <tr>
                <td>Driver Offers</td>
                <td className="text-value">{mpo && mpo.DriverLoadCount > 0
                  ? <>
                    {this.formatPricingCost(mpo.DriverAllInAvgRate, mpo.DriverLoadCount, mpo.DriverMatchLevel, undefined, "accepted offers", "median-based", data.Mode === 'Driver' ? costMatch.background : undefined)}
                    {data.DriverOfferFloorTotal !== null && ` · needs ${formatNumber(data.DriverOfferFloorTotal, "c0")}+ total to pay that`}
                  </>
                  : <span className="px-1 rounded" style={{ background: costMatch.background, color: '#000' }}>No data</span>}</td>
              </tr>}
              {((mpo && mpo.CarrierLoadCount > 0) || data.Mode === 'Carrier') && <tr>
                <td>Carrier Bid Cost</td>
                <td className="text-value">{mpo && mpo.CarrierLoadCount > 0
                  ? this.formatPricingCost(mpo.CarrierAllInAvgRate, mpo.CarrierLoadCount, mpo.CarrierMatchLevel, recommended.Total, "bid samples", "median-based", data.Mode === 'Carrier' ? costMatch.background : undefined)
                  : <span className="px-1 rounded" style={{ background: costMatch.background, color: '#000' }}>No data</span>}</td>
              </tr>}
              <tr><td>Search Range</td><td className="text-value">{formatNumber(data.BandMinTotal, "c0")}–{formatNumber(data.BandMaxTotal, "c0")}</td></tr>
              <tr>
                <td>Market (All Customers)</td>
                <td className="text-value">{mpo && mpo.AllCustomersLoadCount > 0 ? `${formatNumber(data.AllCustomersMinTotal, "c0")}–${formatNumber(data.AllCustomersMaxTotal, "c0")} · ${mpo.AllCustomersLoadCount} loads · ${mpo.AllCustomersMatchLevel}` : "No matches"}</td>
              </tr>
              <tr>
                <td>Market (This Customer)</td>
                <td className="text-value">{mpo && mpo.CurrentCustomerLoadCount > 0 ? `${formatNumber(data.CurrentCustomerMinTotal, "c0")}–${formatNumber(data.CurrentCustomerMaxTotal, "c0")} · ${mpo.CurrentCustomerLoadCount} loads · ${mpo.CurrentCustomerMatchLevel}` : "No matches"}</td>
              </tr>
            </tbody>
          </table>
          <div className="border rounded px-3 py-2 mt-2">
            <div className="font-weight-bold mb-1">Cost Confidence Legend</div>
            {[
              { background: '#22c55e', label: 'PU Zip to DEL Zip Driver/Carrier Cost found' },
              { background: '#e3f6e8', label: 'PU State to DEL Zip Driver/Carrier Cost found' },
              { background: '#f59e0b', label: 'PU State to DEL State Driver/Carrier Cost found' },
              { background: '#dc2626', label: 'Any PU State to DEL State Driver/Carrier Cost found or No Driver/Carrier Cost found' },
            ].map(legend => (
              <div key={legend.background} className="d-flex align-items-center small mb-1">
                <span className="rounded mr-2 flex-shrink-0" style={{ background: legend.background, width: 24, height: 14 }} />
                {legend.label}
              </div>
            ))}
          </div>
        </div>
        <div className="col-12 mt-3">
          <div className="border rounded px-3 py-2">{reason}</div>
        </div>
        {data.Points.length > 0 && <div className="col-12 mt-3">
          <button type="button" className="btn btn-link btn-sm p-0" onClick={() => this.setState(prev => ({ showPricingPoints: !prev.showPricingPoints }))}>
            {this.state.showPricingPoints ? "Hide" : "Show"} how we picked this
          </button>
          {this.state.showPricingPoints && <>
            <table className="table table-sm mt-2 mb-1">
              <thead>
                <tr>
                  <th>Customer Total</th>
                  <th>Win %</th>
                  <th>{data.CostBasis} $</th>
                  <th>Margin</th>
                  <th>Margin $</th>
                  <th>Weighted Score</th>
                </tr>
              </thead>
              <tbody>
                {points.map(point => (
                  <tr key={point.Total} className={point.Total === recommended.Total ? "font-weight-bold" : point.BelowMarginFloor || point.BelowDriverOffer ? "text-muted" : ""}>
                    <td>
                      {formatNumber(point.Total, "c0")}
                      {point.Total === recommended.Total && <span className="badge badge-primary ml-1">Recommended</span>}
                      {point.IsRatingEngine && <span className="badge badge-secondary ml-1">Rating Engine</span>}
                      {point.BelowMarginFloor && <span className="badge badge-warning ml-1">Under {formatNumber(data.MinMargin, "p0")}</span>}
                      {point.BelowDriverOffer && <span className="badge badge-warning ml-1">Below driver offers</span>}
                    </td>
                    <td>{formatNumber(point.WinPercent, "p1")}</td>
                    <td>{formatNumber(point.Cost, "c0")}</td>
                    <td>{formatNumber(point.Total > 0 ? (point.Total - point.Cost) / point.Total : 0, "p1")}</td>
                    <td>{formatNumber(point.Total - point.Cost, "c0")}</td>
                    <td>
                      {eligible.length > 0 && !point.BelowMarginFloor && !point.BelowDriverOffer
                        ? formatNumber(weightedScore(point) * 100, "n1")
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-muted small mb-0">
              Margin $ = customer total − {data.CostBasis.toLowerCase()} $. Weighted score = (win % ÷ best win %) × {formatNumber(winWeight * 100, "n0")} + (margin $ ÷ best margin $) × {formatNumber((1 - winWeight) * 100, "n0")}, where best is across every price searched with at least {formatNumber(data.MinMargin, "p0")} margin{data.DriverOfferPay !== null && " that pays drivers what they typically accept"}. The highest score is recommended.
              {data.CostBasis === "Carrier"
                ? " Carrier $ is based on median carrier bids for this lane."
                : " Driver $ is driver pay like the Rate tab's 67% figure (67% of linehaul plus fuel)."}
              {data.DriverOfferPay !== null && ` Prices below ${formatNumber(data.DriverOfferFloorTotal ?? 0, "c0")} pay the driver less than the ${formatNumber(data.DriverOfferPay, "c0")} median accepted driver offer for this lane, so drivers likely won't take them.`}
            </p>
          </>}
        </div>}
        <div className="col-12">
          <p className="text-muted small mt-3 mb-0">
            How this works: we take the range of recent market totals for this lane, widen it to include the Rating Engine total if needed, and extend the top by 10%. For 50 price points in that range, the win % model predicts our chance of winning. We drop any price under a 20% margin over driver pay (67% of linehaul plus fuel), or over the median-based carrier cost when a carrier covers it (no trucks available, or the destination's virtual terminal has a truck surplus or no data). Each remaining price gets a weighted score, 70% win % and 30% margin $ by default, each measured against the best of the remaining prices, and we recommend the highest score. Drag the slider to see what a different weighting would recommend. This is just a tool, use your judgment, especially when market matches are thin.
          </p>
        </div>
      </div>
    );
  }

  private renderPricingRecommendationSkeleton() {
    return (
      <div className="row">
        <div className="col-md-4">
          <Skeleton shape="text" style={{ width: '50%' }} />
          <Skeleton shape="rectangle" className="mt-2" style={{ width: '70%', height: 40 }} />
          <Skeleton shape="text" className="mt-2" style={{ width: '60%' }} />
          <Skeleton shape="rectangle" className="mt-2" style={{ width: 140, height: 31 }} />
          <Skeleton shape="text" className="mt-3" style={{ width: '100%' }} />
          <Skeleton shape="text" style={{ width: '90%' }} />
        </div>
        <div className="col-md-8">
          {[0, 1, 2, 3, 4, 5].map(i => (
            <div key={i} className="d-flex py-2 border-top">
              <Skeleton shape="text" style={{ width: '30%' }} />
              <Skeleton shape="text" className="ml-3" style={{ width: '55%' }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  private getMpoMarginClass(
    currentMargin: number | null | undefined,
    comparisonMargin: number | null | undefined
  ): string {

    if (currentMargin == null || comparisonMargin == null)
      return "";

    if (currentMargin === comparisonMargin)
      return "";

    return currentMargin > comparisonMargin ? "metric-positive" : "metric-negative";
  }

  private handleTabSelect(e: TabStripSelectEventArguments) {
    if (e.selected === 1 && !this.props.winPercentageData) {
      this.setState({ winPercentageDataLoading: true });
      this.props.fetchWinPercentageChart()
        .then(() => this.setState({ selected: e.selected }))
        .finally(() => this.setState({ winPercentageDataLoading: false }));
    }
    else {
      this.setState({ selected: e.selected });
    }

    if (e.selected === 2 && !this.props.mpoAllInRateData && !this.props.mpoAllInRateLoading) {
      this.props.fetchMpoAllInRate(this.state.mpoLookbackDays);
    }

    if (e.selected === 3 && !this.props.virtualTerminalReport) {
      this.props.fetchVirtualTerminalReport();
    }
  }

  public componentDidMount() {
    fetchApi('/api/Quote/GetChargeTypes')
      .then((chargeTypes: ChargeType[]) => {
        this.setState({ chargeTypes });
      });
  }

  public componentDidUpdate(prevProps: Props) {
    // Rate or transit changed while the MPO tab is open, so its metrics were cleared
    if (this.state.selected === 2 && this.props.data && (this.props.data !== prevProps.data || this.props.distance !== prevProps.distance) && !this.props.mpoAllInRateData && !this.props.mpoAllInRateLoading) {
      this.props.fetchMpoAllInRate(this.state.mpoLookbackDays);
    }
    if (prevProps.pricingRecommendationLoading && !this.props.pricingRecommendationLoading && this.state.pricingModeLoading) {
      this.setState({ pricingModeLoading: null });
    }
  }

  public componentWillReceiveProps(nextProps: Props) {
    if (nextProps.winPercentageData && nextProps.winPercentageData !== this.props.winPercentageData) {
      trackEvent('quote.win_percentage_viewed', {
        QuoteID: this.props.QuoteID,
        BillingCustomerID: this.props.billingCustomerId,
        VehicleTypeID: this.props.vehicleTypeId,
      });
    }
    if (!nextProps.winPercentageData && this.state.selected === 1) {
      this.setState({ selected: 0 });
    }
    if (nextProps.virtualTerminalReport === null && this.state.selected === 3) {
      this.setState({ selected: 0 });
    }
    if (nextProps.data && nextProps.QuoteID != this.props.QuoteID) {
      let allInRate = 0;
      if (nextProps.data.RateComment == 'User Override') {
        if (nextProps.data.LineItems.length == 1) {
          allInRate = nextProps.data.LineItems[0].Rate;
        } else {
          allInRate = nextProps.data.Amount;
        }
      }
      this.setState({ allInRate });
    }
  }

  // Another quote loaded: its own configured weight applies, not the slider from the last one
  public resetPricingWinWeight() {
    this.setState({ pricingWinWeight: null });
  }

  private showModal() {
    this.setState({ showModal: true });
  }

  private hideModal() {
    this.setState({ showModal: false });
  }

  private setPerMileRate() {
    if (this.state.allInRate < 1) {
      alert("Please enter a per mile rate!");
      return;
    }

    if (this.props.distance < 1) {
      alert("Please fix quote mileage!");
      return;
    }

    this.props.replaceCharges([
      {
        ChargeType: this.state.chargeTypes.filter(x => x.RateDescriptionID === 50)[0], // Linehaul
        Quantity: this.props.distance,
        QuantityUoM: 2,
        Rate: this.state.allInRate,
        RateUoM: 8,
        Amount: this.state.allInRate * this.props.distance,
      }]);
  }

  private setAllInRate() {
    if (this.props.data) {

      if (this.state.allInRate < 1) {
        alert("Please enter an all in rate!");
        return;
      }

      this.calculateAllInRate(this.state.allInRate);
    }
  }

  private applyMpoAllInRate(rate: number, button: MpoAllInRateButton) {
    trackEvent('quote.mpo_all_in_rate_applied', {
      Button: button,
      QuoteID: this.props.QuoteID,
      Rate: rate,
      LookbackDays: this.state.mpoLookbackDays,
      BillingCustomerID: this.props.billingCustomerId,
    });

    this.setState({ allInRate: rate }, () => this.setAllInRate());
  }

  private formatFuelRateLineItem(existingRate: QuoteLineItemValue, fuel: number, fuelRate: number): {
    ChargeType: ChargeType,
    Quantity: number,
    QuantityUoM: number,
    Rate: number,
    RateUoM: number,
    Amount: number
  } {
    const rate = {
      ChargeType: this.state.chargeTypes.filter(x => x.RateDescriptionID === existingRate.RateDescriptionID)[0],
      Quantity: 1,
      QuantityUoM: existingRate.QuantityUoM,
      Rate: fuel,
      RateUoM: existingRate.RateUoM,
      Amount: fuel,
    }

    switch (existingRate.RateDescriptionID) {
      case 5000:
        rate.Quantity = existingRate.Quantity;
        rate.Rate = existingRate.Rate;
        break;
      case 5001:
        rate.Quantity = fuelRate > 0 ? (fuel / fuelRate) : 0;
        rate.Rate = fuelRate;
        break;
      case 5003:
        break;
    }

    return rate;
  }

  private updateChargeAmount(e: NumericTextBoxChangeEvent, index: number) {
    this.props.updateCharge(index, { Amount: e.value });
  }

  private removeCharge(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, index: number) {
    e.preventDefault();

    this.props.removeCharge(index);
  }

  private useHistoricalRate(rate: number, rateUpDown: number, previousWinPercentage: number | null = null, currentWinPercentage: number | null = null) {

    if (this.props.distance < 1) {
      alert("Please fix quote mileage!");
      return;
    }

    const linehaul = bankersRounding(rate * this.props.distance);
    let fuel = linehaul * this.props.fuelRate;

    // Reuse Fuel Surcharge Per Mile if it exists
    const existingFuels = this.props.data.LineItems.filter(x => x.RateDescriptionID === 5000 && x.Rate > 0);
    if (existingFuels.length !== 0) {
      fuel = existingFuels[0].Amount;
    } else if (this.props.fuelRate < 0.01) {
      alert("Please fix fuel rate!");
      return;
    }

    const accessorials = this.props.data.LineItems
      .filter(x => x.RateType === 65 && this.state.chargeTypes.find(c => c.RateDescriptionID === x.RateDescriptionID))
      .reduce((a, b) => a + b.Amount, 0);

    this.calculateAllInRate(linehaul + fuel + accessorials, true, rateUpDown, previousWinPercentage, currentWinPercentage);
  }

  public calculateAllInRate(total: number, smartbid: boolean = false, rateUpDown: number | null = null, previousWinPercentage: number | null = null, currentWinPercentage: number | null = null) {
    const rebateLineItem = this.props.data.LineItems.find(x => x.RateDescriptionID === 50609);
    const existingAccessorials = this.props.data.LineItems.filter(x => x.RateType === 65 && this.state.chargeTypes.find(c => c.RateDescriptionID === x.RateDescriptionID) && x.RateDescriptionID !== 50609);
    const allInRate = total - existingAccessorials.reduce((a, b) => a + b.Amount, 0);
    if (allInRate < 1) {
      alert("Please enter an all in rate that is greater than the existing accessorials!");
      return;
    }

    // Ensure input matches total if called from quote main
    if (!smartbid) {
      this.setState({ allInRate });
    }

    // When a rebate exists the backend computes fuel on net linehaul, so solve for gross accordingly:
    // grossLH = allInRate / ((1 - r) * (1 + f) + r)
    const r = rebateLineItem?.Rate ?? 0;
    if (r >= 1 || r < 0) {
      alert("Unable to calculate because the rebate rate is invalid.");
      return;
    }
    let linehaul = Math.round(allInRate / ((1 - r) * (1 + this.props.fuelRate) + r) * 100) / 100;
    let fuel = Math.round(linehaul * (1 - r) * this.props.fuelRate * 100) / 100;

    // Validate total and adjust if necessary
    const allInCents = Math.round(allInRate * 100);
    const calculatedTotalCents = (linehaul + fuel) * 100;
    if (calculatedTotalCents !== allInCents) {
      const adjustment = allInCents - calculatedTotalCents;
      fuel += adjustment / 100;
    }

    // Read current fuel rates if any and just update with the same style
    const existingFuels = this.props.data.LineItems.filter(x => [5000, 5001, 5003].includes(x.RateDescriptionID) && x.Rate > 0);

    // Existing fuel not found
    if (existingFuels.length !== 1) {
      this.props.replaceCharges([
        {
          ChargeType: this.state.chargeTypes.find(x => x.RateDescriptionID === 50), // Linehaul
          Quantity: 1,
          QuantityUoM: 1,
          Rate: rebateLineItem ? Math.round(linehaul * (1 - rebateLineItem.Rate) * 100) / 100 : linehaul,
          RateUoM: 8,
          Amount: rebateLineItem ? Math.round(linehaul * (1 - rebateLineItem.Rate) * 100) / 100 : linehaul
        },
        {
          ChargeType: this.state.chargeTypes.find(x => x.RateDescriptionID === 5003), // Fuel
          Quantity: 1,
          QuantityUoM: 1,
          Rate: fuel,
          RateUoM: 8,
          Amount: fuel
        },
        ...(rebateLineItem ? [{ ChargeType: this.state.chargeTypes.find(x => x.RateDescriptionID === 50609), Quantity: linehaul, QuantityUoM: rebateLineItem.QuantityUoM, Rate: rebateLineItem.Rate, RateUoM: rebateLineItem.RateUoM, Amount: Math.round(linehaul * rebateLineItem.Rate * 100) / 100 }] : []),
        ...existingAccessorials.map(existingAccessorial => ({
          ChargeType: this.state.chargeTypes.find(x => x.RateDescriptionID === existingAccessorial.RateDescriptionID),
          Quantity: existingAccessorial.Quantity,
          QuantityUoM: existingAccessorial.QuantityUoM,
          Rate: existingAccessorial.Rate,
          RateUoM: existingAccessorial.RateUoM,
          Amount: existingAccessorial.Amount
        }))
      ], smartbid, rateUpDown, previousWinPercentage, currentWinPercentage);
    } else {
      // Special case for existing Fuel Surcharge Per Mile
      if (existingFuels[0].RateDescriptionID === 5000) {
        fuel = existingFuels[0].Amount;
        linehaul = allInRate - fuel;
      }

      this.props.replaceCharges([
        {
          ChargeType: this.state.chargeTypes.filter(x => x.RateDescriptionID === 50)[0], // Linehaul
          Quantity: 1,
          QuantityUoM: 1,
          Rate: rebateLineItem ? Math.round(linehaul * (1 - rebateLineItem.Rate) * 100) / 100 : linehaul,
          RateUoM: 8,
          Amount: rebateLineItem ? Math.round(linehaul * (1 - rebateLineItem.Rate) * 100) / 100 : linehaul
        },
        this.formatFuelRateLineItem(existingFuels[0], fuel, this.props.fuelRate),
        ...(rebateLineItem ? [{ ChargeType: this.state.chargeTypes.find(x => x.RateDescriptionID === 50609), Quantity: linehaul, QuantityUoM: rebateLineItem.QuantityUoM, Rate: rebateLineItem.Rate, RateUoM: rebateLineItem.RateUoM, Amount: Math.round(linehaul * rebateLineItem.Rate * 100) / 100 }] : []),
        ...existingAccessorials.map(existingAccessorial => ({
          ChargeType: this.state.chargeTypes.find(x => x.RateDescriptionID === existingAccessorial.RateDescriptionID),
          Quantity: existingAccessorial.Quantity,
          QuantityUoM: existingAccessorial.QuantityUoM,
          Rate: existingAccessorial.Rate,
          RateUoM: existingAccessorial.RateUoM,
          Amount: existingAccessorial.Amount
        }))
      ], smartbid, rateUpDown, previousWinPercentage, currentWinPercentage);
    }
  }
}
